/** Copyright © 2025 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/

import type { AxiosInstance } from 'axios';

import { Service } from '@hems/service/src/api/Service';
import { apiWrapper } from '@hems/service/src/util/apiHelper';

import type { CommonResponseWrapper } from 'hems';

import type {
  ApplianceItemList,
  ApplianceCommandsParams,
  ApplianceChartParams,
  ApplianceChartResponse,
  ApplianceRefreshTokenResponse,
  SmartThingsComponents,
  SmartThingsDeviceInfo,
  SmartThingsHealthStatusInfo,
  SmartThingsLocationInfo,
  SmartThingsLocationListResponse,
  SmartThingsRoomInfo,
  SmartThingsRoomListResponse,
  SmartThingsControlResponse,
} from 'hems/device';

export default class HomeApplianceService extends Service {
  constructor(axios: AxiosInstance) {
    super(axios);
  }

  /** 가전 목록 조회 */
  getApplianceList = async (siteId: number): Promise<ApplianceItemList> => {
    const { data } = await this.get<CommonResponseWrapper<ApplianceItemList>>(
      apiWrapper.managerApi(`/smart-things/${siteId}`)
    );

    return data;
  };

  /** 가전 연결 상태 조회 */
  getApplianceStatus = async (deviceId: string): Promise<SmartThingsComponents> => {
    return await this.get<SmartThingsComponents>(`/devices/${deviceId}/status`);
  };

  /** 가전 정보 조회 */
  getApplianceInfo = async (deviceId: string): Promise<SmartThingsDeviceInfo> => {
    return await this.get<SmartThingsDeviceInfo>(`/devices/${deviceId}`);
  };

  /** 가전 상태 조회 */
  getApplianceHealth = async (deviceId: string): Promise<SmartThingsHealthStatusInfo> => {
    return await this.get<SmartThingsHealthStatusInfo>(`/devices/${deviceId}/health`);
  };

  /** 가전 위치 목록 조회 */
  getApplianceLocations = async (): Promise<SmartThingsLocationInfo[]> => {
    const { items } = await this.get<SmartThingsLocationListResponse>('/locations');

    return items;
  };

  /** 가전 방 목록 조회 */
  getApplianceRooms = async (locationId: string): Promise<SmartThingsRoomInfo[]> => {
    const { items } = await this.get<SmartThingsRoomListResponse>(`/locations/${locationId}/rooms`);

    return items;
  };

  /** 가전 제어 */
  controlAppliance = async (deviceId: string, params: ApplianceCommandsParams): Promise<SmartThingsControlResponse> => {
    return await this.post<SmartThingsControlResponse>(`/devices/${deviceId}/commands`, params);
  };

  /** 가전 토큰 갱신 */
  refreshSmartThingsToken = async (token: string): Promise<ApplianceRefreshTokenResponse> => {
    const { data } = await this.post<CommonResponseWrapper<ApplianceRefreshTokenResponse>>(
      apiWrapper.managerApi('/smart-things/tokens/refresh'),
      { token }
    );

    return data;
  };

  /** 가전 차트 조회 */
  getApplianceChart = async (siteId: number, params: ApplianceChartParams): Promise<ApplianceChartResponse> => {
    const { data } = await this.get<CommonResponseWrapper<ApplianceChartResponse>>(
      apiWrapper.managerApi(`/smart-things/charts/${siteId}`),
      params
    );

    return data;
  };

  /** 가전 별 차트 조회 */
  getChartByAppliance = async (
    applianceDeivceId: string,
    params: ApplianceChartParams
  ): Promise<ApplianceChartResponse> => {
    const { data } = await this.get<CommonResponseWrapper<ApplianceChartResponse>>(
      apiWrapper.managerApi(`/smart-things/charts/${applianceDeivceId}/powers`),
      params
    );

    return data;
  };
}
